import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { BrowserLoader as Browser } from '@atlaskit/media-picker/browser';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ITEM_FIELDS } from '../../../../types';
import type { UpdateItemFormFieldType } from '../../../items-shared/types';
import {
	type SelectedImage,
	type UnsplashResult,
	UnsplashSearch,
	type UploadedImage,
	useUploadMediaConfig,
} from '../../../media-components';
import { messages } from './messages';

const styles = cssMap({
	fieldWrapper: {
		marginTop: token('space.200'),
	},
});

export type UnsplashSearchFieldProps = {
	analyticsSource: string;
	confluencePageId?: string;
	contentId: string;
	idPrefix: string;
	imageAltText?: string;
	imageSrc?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	isNoImageAllowed: boolean;
	itemId: string;
	updateItemFormField: UpdateItemFormFieldType;
};

export const UnsplashSearchField = ({
	analyticsSource,
	idPrefix,
	itemId,
	confluencePageId,
	contentId,
	imageSrc,
	imageAltText,
	initialUnsplashResults,
	isNoImageAllowed,
	updateItemFormField,
}: UnsplashSearchFieldProps): React.JSX.Element => {
	const intl = useIntl();

	const handleUnsplashImageSelection = useCallback(
		({ fullSizeImageUrl, altDescription }: SelectedImage) => {
			updateItemFormField(itemId, {
				[ITEM_FIELDS.IMAGE_SRC]: fullSizeImageUrl,
				[ITEM_FIELDS.IMAGE_ALT_TEXT]: altDescription,
			});
		},
		[itemId, updateItemFormField],
	);

	const handleUploadedImageSelection = useCallback(
		({ uploadedImageId, altDescription }: UploadedImage) => {
			updateItemFormField(itemId, {
				[ITEM_FIELDS.IMAGE_SRC]: uploadedImageId,
				[ITEM_FIELDS.IMAGE_ALT_TEXT]: altDescription,
			});
		},
		[itemId, updateItemFormField],
	);

	const {
		mediaConfig,
		handleUploadEnd,
		handleUploadStart,
		handleOnError,
		isMediaBrowserOpen,
		openMediaBrowser,
		closeMediaBrowser,
		isUploadingImage,
	} = useUploadMediaConfig({
		contentId,
		handleUploadedImageSelection,
		analyticsSource,
	});

	return (
		<>
			<Flex xcss={cx(styles.fieldWrapper)} direction="column">
				<UnsplashSearch
					id={itemId}
					selectedImageUrl={imageSrc}
					imageAltText={imageAltText}
					sectionLabel={intl.formatMessage(messages.sectionLabel)}
					initialUnsplashResults={initialUnsplashResults}
					idPrefix={idPrefix}
					handleImageSelection={handleUnsplashImageSelection}
					analyticsSource={analyticsSource}
					analyticsActionSubject="button"
					analyticsActionSubjectId="customerImageUpload"
					openMediaBrowser={openMediaBrowser}
					contentId={contentId}
					confluencePageId={confluencePageId}
					isUploadingImage={isUploadingImage}
					isNoImageAllowed={isNoImageAllowed}
				/>
				{mediaConfig?.collection ? (
					<Browser
						mediaClientConfig={{
							authProvider: mediaConfig?.authProvider,
							useSha256ForUploads: true,
						}}
						config={{
							multiple: false,
							fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
							uploadParams: {
								collection: mediaConfig.collection,
								onUploadRejection: mediaConfig?.onUploadRejection,
							},
						}}
						isOpen={isMediaBrowserOpen}
						onClose={closeMediaBrowser}
						onUploadsStart={handleUploadStart}
						onError={handleOnError}
						onEnd={handleUploadEnd}
					/>
				) : null}
			</Flex>
		</>
	);
};
